/* Stream playback over an ordered list of sources:
   - Safari/iOS: native HLS (Apple's own player), then the Icecast MP3 stream.
   - elsewhere:  hls.js (MSE), then native HLS if the browser has it, then the Icecast MP3 stream.
   A watchdog moves to the next source when playback errors or stops advancing for STALL_MS (a source that
   "plays" but makes no progress is the failure mode that never raises an error), and wraps around after the last.
   Network blips: hls.js retries itself; other fatal errors restart after a short wait. */
import Hls from 'hls.js';
import { createSignal } from 'solid-js';

// Safari (macOS and iOS; every iOS browser is WebKit). Excludes Chromium-based browsers, which also say "Safari".
export const IS_WEBKIT = /iPad|iPhone|iPod/.test(navigator.userAgent)
  || /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);

// Where the meters get their levels (see meters.ts). Web Audio needs the element routed through an AudioContext;
// Safari's gets only zeros from the stream, so Safari follows the precomputed envelopes and keeps its native HLS
// player.
export const WEBAUDIO_METERS = !IS_WEBKIT;

export type StreamState = 'idle' | 'starting' | 'playing' | 'buffering' | 'blocked' | 'offline' | 'paused';
type Source = 'hlsjs' | 'native' | 'mp3';

const STALL_MS = 12_000;

export function createStreamPlayer() {
  const audio = new Audio();
  // Only the Web Audio meters need CORS-clean media; leave Safari's element as plain as the library player that is
  // known to work there.
  if (WEBAUDIO_METERS) audio.crossOrigin = 'anonymous';
  audio.preload = 'none';
  audio.setAttribute('playsinline', '');
  // In the document, like the library player that plays on Safari; a detached element is one difference less.
  audio.style.display = 'none';
  document.body.appendChild(audio);
  const [state, setState] = createSignal<StreamState>('idle');
  const [source, setSource] = createSignal<Source | null>(null);
  const [playingDate, setPlayingDate] = createSignal<Date | null>(null);
  let hls: Hls | null = null;
  let hlsUrl = '';
  let mp3Url = '';
  let retry: number | undefined;
  let wanted = false;
  let idx = 0;
  let lastTime = -1;
  let lastProgress = 0;
  // Wall-clock time of currentTime 0 on the stream's timeline (program date time), so the audible moment can be
  // read at any instant (meters), not only at the last timeupdate.
  let epoch: number | null = null;
  // Our own teardown fires pause/emptied/AbortError as later tasks; those are not the browser's verdict.
  let switchingUntil = 0;
  const switching = () => Date.now() < switchingUntil;

  const nativeHls = audio.canPlayType('application/vnd.apple.mpegurl') !== '';
  const order: Source[] = IS_WEBKIT && !WEBAUDIO_METERS
    ? [...(nativeHls ? ['native' as const] : []), 'mp3']
    : [...(Hls.isSupported() ? ['hlsjs' as const] : []), ...(nativeHls ? ['native' as const] : []), 'mp3'];

  audio.addEventListener('playing', () => { if (!audio.paused) { setState('playing'); lastProgress = Date.now(); } });
  // Safari's autoplay block doesn't reject play(): it fires playing, then pauses the element before loading
  // anything. A pause we didn't ask for means the browser wants a tap.
  audio.addEventListener('pause', () => { if (wanted && !switching() && !audio.ended) setState('blocked'); });
  audio.addEventListener('waiting', () => wanted && setState('buffering'));
  // No 'stalled' -> buffering: Safari fires it every few seconds on live HLS while the buffer is full and playing on.
  audio.addEventListener('timeupdate', () => {
    if (audio.currentTime !== lastTime) {
      lastTime = audio.currentTime;
      lastProgress = Date.now();
      if (state() === 'buffering' && !audio.paused) setState('playing');
    }
    if (hls?.playingDate) setPlayingDate(hls.playingDate);
    else if (source() === 'native' && (audio as any).getStartDate) {
      const sd: Date = (audio as any).getStartDate();
      if (sd && !isNaN(sd.getTime())) setPlayingDate(new Date(sd.getTime() + audio.currentTime * 1000));
    } else setPlayingDate(null); // MP3: no timeline in the stream; now-playing follows the wall clock
    const pd = playingDate();
    epoch = pd ? pd.getTime() - audio.currentTime * 1000 : null;
  });
  audio.addEventListener('error', () => wanted && !hls && next());

  // Watchdog: playing (or trying to) but the clock hasn't moved for STALL_MS -> next source.
  window.setInterval(() => {
    if (!wanted || audio.paused || state() === 'blocked' || state() === 'paused' || state() === 'idle') return;
    if (Date.now() - lastProgress > STALL_MS) next();
  }, 2000);

  function next() {
    idx = (idx + 1) % order.length;
    scheduleRestart(1000);
  }

  function scheduleRestart(ms = 5000) {
    setState('offline');
    clearTimeout(retry);
    retry = window.setTimeout(() => wanted && start(), ms);
  }

  async function playElement() {
    // Safari may neither play nor reject (the promise never settles): if the element is still paused shortly
    // after asking, the browser wants a user gesture.
    window.setTimeout(() => { if (wanted && audio.paused && state() !== 'paused') setState('blocked'); }, 1500);
    try {
      await audio.play();
    } catch (e: any) {
      if (e?.name === 'NotAllowedError') setState('blocked'); // autoplay policy: needs a user gesture
      else if (e?.name !== 'AbortError' && !switching()) next(); // AbortError: we replaced the source ourselves
    }
  }

  // currentTime advances in coarse steps on some browsers (Safari's native HLS among them); meters read the clock
  // 60 times a second. Run our own clock from performance.now(), pulled gently towards currentTime when it moves
  // and reset when they part by more than a stutter (seek, stall, source change).
  let clockMs = 0;
  let clockAt = 0;
  let seenCt = -1;
  function mediaMs() {
    const now = performance.now();
    const ct = audio.currentTime * 1000;
    let t = clockMs + (now - clockAt) * audio.playbackRate;
    if (ct !== seenCt) {
      seenCt = ct;
      t = Math.abs(ct - t) > 250 ? ct : t + (ct - t) * 0.1;
    }
    t = Math.min(t, ct + 300); // currentTime stopped (buffering): don't run off
    clockMs = t;
    clockAt = now;
    return t;
  }

  function start() {
    teardown();
    wanted = true;
    setState('starting');
    lastProgress = Date.now();
    lastTime = -1;
    epoch = null;
    const src = order[idx];
    setSource(src);
    if (src === 'hlsjs') {
      hls = new Hls({ liveSyncDurationCount: 2, liveMaxLatencyDurationCount: 6, manifestLoadingMaxRetry: 6,
        levelLoadingMaxRetry: 6, fragLoadingMaxRetry: 6, lowLatencyMode: false });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
        else scheduleRestart();
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => void playElement());
    } else {
      audio.src = src === 'native' ? hlsUrl : mp3Url;
      void playElement();
    }
  }

  function teardown() {
    clearTimeout(retry);
    switchingUntil = Date.now() + 500;
    if (hls) { hls.destroy(); hls = null; }
    audio.removeAttribute('src');
    audio.load();
  }

  return {
    audio,
    state,
    source,
    playingDate,
    /** The wall-clock moment being heard now (ms), or null when not playing or the source has no timeline (MP3). */
    audibleMs: () => (epoch !== null && state() === 'playing' && !audio.paused ? epoch + mediaMs() : null),
    setUrls(h: string, m: string) { hlsUrl = h; mp3Url = m; },
    start,
    stop() { wanted = false; teardown(); setState('idle'); },
    resume() { lastProgress = Date.now(); void playElement(); },
    setVolume(v: number) { audio.volume = Math.max(0, Math.min(1, v)); },
  };
}

export type StreamPlayer = ReturnType<typeof createStreamPlayer>;
